import React, { useState, useEffect } from 'react';
import { Plus, X as XIcon } from 'lucide-react';
import { useLanguage } from '../../i18n/index.jsx';
import contactService from '../../services/contactService';
import { userErrorMessage } from '../../utils/userError';
import Modal from '../ui/Modal';

// Shared contact-form primitives for the Contacts Hub (Agenda TO Contacts —
// Phase B). Reused by the "New contact" dialog AND the relationship-management
// edit overlay. Deliberately DOES NOT expose the manual PAI-username /
// omnis_user_id linking field — Phase A made the handshake the only path to
// Network, so manual linking is deprecated and kept out of the payload (FE-4).

// A method list (emails/phones) always keeps at least one (blank) row in the form.
const methodsFromContact = (arr, single) => {
    const list = (Array.isArray(arr) && arr.length) ? arr : (single ? [single] : []);
    return list.length ? list : [''];
};

export const blankForm = () => ({
    name: '',
    company: '',
    position: '',
    industry: '',
    birthday: '',
    phones: [''],
    emails: [''],
    notes: '',
    socialTwitter: '',
    socialLinkedin: '',
    socialInstagram: '',
    is_favorite: false,
});

export const formFromContact = (c) => ({
    name: c.name || '',
    company: c.company || '',
    position: c.position || '',
    industry: c.industry || '',
    birthday: c.birthday || '',
    phones: methodsFromContact(c.phones, c.phone),
    emails: methodsFromContact(c.emails, c.email),
    notes: c.notes || '',
    socialTwitter: (c.social_handles?.twitter || c.social_handles?.x || ''),
    socialLinkedin: (c.social_handles?.linkedin || ''),
    socialInstagram: (c.social_handles?.instagram || ''),
    is_favorite: !!c.is_favorite,
});

export const formToPayload = (form) => {
    const socials = {};
    if (form.socialTwitter.trim()) socials.twitter = form.socialTwitter.trim();
    if (form.socialLinkedin.trim()) socials.linkedin = form.socialLinkedin.trim();
    if (form.socialInstagram.trim()) socials.instagram = form.socialInstagram.trim();
    const clean = (arr) => (arr || []).map((s) => s.trim()).filter(Boolean);
    return {
        name: form.name.trim(),
        company: form.company.trim() || null,
        position: form.position.trim() || null,
        industry: (form.industry || '').trim() || null,
        // Birthday is an ISO YYYY-MM-DD date input; empty string clears it.
        birthday: form.birthday ? form.birthday : null,
        phones: clean(form.phones),
        emails: clean(form.emails),
        notes: form.notes.trim() || null,
        social_handles: Object.keys(socials).length > 0 ? socials : null,
        is_favorite: !!form.is_favorite,
    };
};

// Dynamic add/remove list of contact methods (emails or phones) — no fixed
// limit on entries (More emails and Phone numbers ticket).
const MethodList = ({ label, type, values, onChange }) => {
    const { t } = useLanguage();
    const rows = values.length ? values : [''];
    const setAt = (i, v) => onChange(rows.map((r, j) => (j === i ? v : r)));
    const removeAt = (i) => { const next = rows.filter((_, j) => j !== i); onChange(next.length ? next : ['']); };
    const testKind = type === 'email' ? 'email' : 'phone';
    return (
        // #539: min-w-0 down the chain — an <input> in a flex row otherwise keeps
        // its intrinsic ~20-character width and spills out of its grid column
        // (Phones ran into Emails, Emails past the card's edge).
        <div className="block min-w-0" data-testid={`contact-${testKind}-list`}>
            <span className="text-[11px] font-medium text-pai-meta">{label}</span>
            <div className="mt-1 space-y-1">
                {rows.map((v, i) => (
                    <div key={i} className="flex items-center gap-1 min-w-0">
                        <input
                            type={type}
                            value={v}
                            data-testid={`contact-${testKind}-${i}`}
                            onChange={(e) => setAt(i, e.target.value)}
                            className="flex-1 min-w-0 w-full px-2 py-1.5 text-sm pai-input"
                        />
                        {rows.length > 1 && (
                            <button
                                type="button"
                                onClick={() => removeAt(i)}
                                aria-label={t('auxRemoveMethod', 'Remove {label} {n}', { label: label.toLowerCase(), n: i + 1 })}
                                className="flex-shrink-0 p-1 text-pai-faint hover:text-pai-red transition-colors"
                            >
                                <XIcon size={14} />
                            </button>
                        )}
                    </div>
                ))}
                <button
                    type="button"
                    onClick={() => onChange([...rows, ''])}
                    className="inline-flex items-center gap-1 text-xs text-pai-blue hover:underline"
                >
                    <Plus size={12} /> {t('auxAddMethod', 'Add {label}', { label: label.toLowerCase().replace(/s$/, '') })}
                </button>
            </div>
        </div>
    );
};

export const ContactFormFields = ({ form, setForm }) => {
    const { t } = useLanguage();
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    return (
        <>
            <label className="block">
                <span className="text-[11px] font-medium text-pai-meta">{t('auxContactNameRequired', 'Name *')}</span>
                <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    className="mt-1 w-full px-2 py-1.5 text-sm pai-input"
                    required
                />
            </label>
            <div className="grid grid-cols-2 gap-2">
                <label className="block">
                    {/* #505: the company the contact works for — was mislabelled "Workspace". */}
                    <span className="text-[11px] font-medium text-pai-meta">{t('contactFieldCompany')}</span>
                    <input
                        type="text"
                        value={form.company}
                        data-testid="contact-company-input"
                        onChange={(e) => update('company', e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 text-sm pai-input"
                    />
                </label>
                <label className="block">
                    <span className="text-[11px] font-medium text-pai-meta">{t('contactFieldPosition')}</span>
                    <input
                        type="text"
                        value={form.position}
                        data-testid="contact-position-input"
                        onChange={(e) => update('position', e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 text-sm pai-input"
                    />
                </label>
            </div>
            {/* #505: the sector the contact works in — never stored as Position. */}
            <label className="block">
                <span className="text-[11px] font-medium text-pai-meta">{t('contactFieldIndustry')}</span>
                <input
                    type="text"
                    value={form.industry || ''}
                    data-testid="contact-industry-input"
                    onChange={(e) => update('industry', e.target.value)}
                    className="mt-1 w-full px-2 py-1.5 text-sm pai-input"
                />
            </label>
            <label className="block">
                <span className="text-[11px] font-medium text-pai-meta">{t('contactFieldBirthday')}</span>
                <input
                    type="date"
                    value={form.birthday || ''}
                    data-testid="contact-birthday-input"
                    onChange={(e) => update('birthday', e.target.value)}
                    className="mt-1 w-full px-2 py-1.5 text-sm pai-input"
                />
            </label>
            <div className="grid grid-cols-2 gap-2 items-start">
                <MethodList label="Phones" type="tel" values={form.phones} onChange={(v) => update('phones', v)} />
                <MethodList label="Emails" type="email" values={form.emails} onChange={(v) => update('emails', v)} />
            </div>

            <details className="border border-pai-subtle rounded-chip">
                <summary className="px-2 py-1.5 text-xs text-pai-meta cursor-pointer select-none">
                    {t('auxSocialHandles', 'Social handles')}
                </summary>
                <div className="px-2 py-2 space-y-2">
                    {['Twitter / X', 'LinkedIn', 'Instagram'].map((label, idx) => {
                        const key = ['socialTwitter', 'socialLinkedin', 'socialInstagram'][idx];
                        return (
                            <label key={key} className="block">
                                <span className="text-[10px] font-medium text-pai-meta uppercase tracking-wide">{label}</span>
                                <input
                                    type="text"
                                    value={form[key]}
                                    onChange={(e) => update(key, e.target.value)}
                                    className="mt-0.5 w-full px-2 py-1 text-xs pai-input"
                                />
                            </label>
                        );
                    })}
                </div>
            </details>

            <label className="block">
                <span className="text-[11px] font-medium text-pai-meta">{t('commonNotes', 'Notes')}</span>
                <textarea
                    value={form.notes}
                    onChange={(e) => update('notes', e.target.value)}
                    rows={3}
                    className="mt-1 w-full px-2 py-1.5 text-sm pai-input resize-none"
                />
            </label>

            <label className="flex items-center gap-2 text-xs text-pai-secondary cursor-pointer">
                <input
                    type="checkbox"
                    checked={form.is_favorite}
                    onChange={(e) => update('is_favorite', e.target.checked)}
                    className="w-4 h-4 accent-[#F5A524] rounded-chip"
                />
                {t('auxAddToFavorites', 'Add to favorites')}
            </label>
        </>
    );
};

// The "New contact" dialog (unchanged behaviour — replaces the old header "+ New"
// button; now reached from the command-bar three-dots menu, FE-1).
export const ManualContactDialog = ({ open, onClose, onSaved }) => {
    const { t } = useLanguage();
    const [form, setForm] = useState(blankForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Duplicate Guard (#260): the backend refuses a Name+Email / Name+Phone match
    // with a 409 + the existing entry, and the user picks what happens next.
    const [duplicate, setDuplicate] = useState(null);

    useEffect(() => {
        if (open) {
            setForm(blankForm());
            setError(null);
            setDuplicate(null);
        }
    }, [open]);

    if (!open) return null;

    const handleSave = async (e, override = {}) => {
        e?.preventDefault();
        if (!form.name.trim()) {
            setError('Name is required.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = { ...formToPayload(form), ...override };
            const created = await contactService.createContact(payload);
            setDuplicate(null);
            onSaved?.(created);
            onClose?.();
        } catch (err) {
            if (err?.status === 409 && err?.body?.duplicate) {
                setDuplicate({ ...err.body.duplicate, prompt: err.message });
            } else {
                setError(userErrorMessage(err, t));
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal onClose={onClose} zIndex="z-[80]" layout="column" cardClassName="max-h-[90dvh]" cardTestId="manual-contact-dialog">
                <div className="flex items-center justify-between p-4 border-b border-pai-subtle">
                    <h3 className="text-base font-semibold text-white">{t('contactsMenuNewContact', 'New contact')}</h3>
                    <button onClick={onClose} className="text-pai-faint hover:text-white p-1 transition-colors">
                        <XIcon size={16} />
                    </button>
                </div>
                <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-4 space-y-3">
                    <ContactFormFields form={form} setForm={setForm} />
                    {error && <div className="text-xs text-pai-red">{error}</div>}
                    {duplicate && (
                        <div
                            data-testid="manual-contact-duplicate"
                            className="rounded-ctl border border-pai-amber/40 bg-pai-amber/[0.08] p-2 text-xs text-pai-amber"
                        >
                            <div>{duplicate.prompt}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    data-testid="manual-contact-duplicate-save-anyway"
                                    onClick={(e) => handleSave(e, { allow_duplicate: true })}
                                    disabled={saving}
                                    className="px-2.5 py-1 font-semibold rounded-chip bg-pai-amber/[0.1] border border-pai-amber/40 text-pai-amber hover:bg-pai-amber/[0.18] disabled:opacity-50 transition-colors"
                                >
                                    {t('contactDuplicateSaveAnyway', 'Save duplicate anyway')}
                                </button>
                                <button
                                    type="button"
                                    data-testid="manual-contact-duplicate-update"
                                    onClick={(e) => handleSave(e, { update_existing: true })}
                                    disabled={saving}
                                    className="px-2.5 py-1 font-semibold rounded-chip bg-pai-amber/[0.1] border border-pai-amber/40 text-pai-amber hover:bg-pai-amber/[0.18] disabled:opacity-50 transition-colors"
                                >
                                    {t('contactDuplicateUpdate', 'Update existing entry')}
                                </button>
                            </div>
                        </div>
                    )}
                </form>
                <div className="px-4 py-3 border-t border-pai-subtle flex justify-end gap-2 bg-white/[0.025]">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-pai-secondary hover:bg-white/[0.07] rounded-chip">
                        {t('commonCancel', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || !form.name.trim()}
                        className="pai-btn-primary disabled:opacity-50"
                    >
                        {saving ? 'Saving…' : 'Create'}
                    </button>
                </div>
            </Modal>
    );
};
